import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button, Col, Form, Input, Row, Select } from 'antd';
import InputNumberFull from '../atoms/InputNumberFull';
import RowSection from '../atoms/RowSection';
import { FormInstance } from 'antd/lib/form';
import styled from 'styled-components';
import addressRepo from '../../services/repository/address';
import { TypePublicPlace } from '../../interfaces/TypePublicPlace';
import { State } from '../../interfaces/State';
import { CountryBirth } from '../../interfaces/CountryBirth';
import { City } from '../../interfaces/City';
import { AddressData } from '../../interfaces/AddressData';
import { AddressDataRequest } from '../../interfaces/AddressDataRequest';
import personalDataRepo from '../../services/repository/personalData';
import { validateMessages } from '../../services/util/validator';
import { AdmissionDataRules } from '../../services/util/field-rules-manager/types/fieldRuleTypes';
import AdmissionFormItem from '../molecules/AdmissionFormItem';
import { events } from '../../services/util/field-rules-manager/FieldRuleService';
import { isScreenUnderWidth } from '../../services/util/screen';
import DefaultSelectCountry from '../../constants/DefaultSelectCountry';

const { Option } = Select;

const ButtonCol = styled(Col)`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 24 },
  },
};

type Props = {
  handleNext: () => void,
  initForm: AddressDataRequest | undefined,
  admissionDataRules: AdmissionDataRules
};

const Address = React.forwardRef<FormInstance, Props>(({ handleNext, initForm, admissionDataRules }, ref) => {
  const formRules = admissionDataRules.address;
  const [data, setData] = useState<{
    countries: CountryBirth[],
    typePublicPlace: TypePublicPlace[],
    states: State[],
    cities: City[],
  }>({ countries: [], typePublicPlace: [], states: [], cities: [] });
  const [form] = Form.useForm<AddressDataRequest | undefined>();

  // @ts-ignore
  useImperativeHandle(ref, () => ({
    validateFields: () => form.validateFields(),
    getFieldsValue: form.getFieldsValue,
    submit: () => form.submit(),
  }));

  const handleCepChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    // Remove qualquer caractere não numérico do valor do CEP
    const formattedCep = value.replace(/\D/g, '');
    // Adiciona a máscara de CEP (XXXXX-XXX)
    const maskedCep = formattedCep.replace(/(\d{5})(\d{3})/, '$1-$2');
    // Atualiza o estado do CEP
    form.setFieldValue('cep', maskedCep);
  };

  const getAddressByCEP = async (e: React.FocusEvent<HTMLInputElement>): Promise<AddressData | undefined> => {
    try {
      e.preventDefault();

      const cep: string = e.target.value;
      const response = await addressRepo.findAddress(cep);

      const { bairro, complemento, logradouro, uf, localidade } = response.data;
      form.setFieldsValue({
        country: DefaultSelectCountry.id,
        neighborhood: bairro,
        complement: complemento,
        street: logradouro,
        state: uf,
      });

      // toggles the loading of the cities options
      if(uf){
        await onChangeState(uf, localidade);
      }
      return;
    } catch (error) {
      console.error('Erro ao buscar endereço:', error);
      return undefined;
    }
  };

  const fetchData = async () => {
    try {
      const [countries, typePublicPlace, states] = await Promise.all([
        addressRepo.findAllCountries(),
        addressRepo.findAllTypePublicPlace(),
        addressRepo.findAllStates(),
      ]);

      let cities: City[] = [];

      if (initForm?.state) {
        cities = await personalDataRepo.findAllCities(initForm?.state);
      }

      setData({
        countries: countries.data,
        typePublicPlace: typePublicPlace.data,
        states: states.data,
        cities: cities,
      });
    } catch (error) {
      console.error('Ocorreu um erro ao buscar os dados:', error);
    }
  };

  useEffect(() => {
    fetchData().then(r => r);
  }, []);

  useEffect(() => {
    form.setFieldsValue(initForm);
  }, [initForm]);

  const EXTERIOR_STATE_ID = 'EX';

  const onChangeState = async (value: string, alreadySelectedCity?: string) => {
    if (value === EXTERIOR_STATE_ID) {
      form.setFieldValue('country', undefined);
    } else if (value) {
      form.setFieldValue('country', DefaultSelectCountry.id);
    }

    const cities = await personalDataRepo.findAllCities(value);
    setData({
      ...data, cities: cities,
    });

    if (alreadySelectedCity) {
      const cityToAdd = cities.find(
        (city) => city.description.toLowerCase() === alreadySelectedCity.toLowerCase(),
      );
      form.setFieldValue('city', cityToAdd?.id);
    }
  };

  const onValuesChangeHandler = (changedValues: any) => {
    const watchedFields = [
      formRules.state?.name,
    ];

    if (Object.keys(changedValues).some(changedField => watchedFields.includes(changedField))) {
      events.triggerFormRuleUpdateEvent();
    }
  };

  const isUnder = isScreenUnderWidth(576);

  return (
    <>
      <Form
        onValuesChange={onValuesChangeHandler}
        form={form}
        {...formItemLayout}
        style={{ maxWidth: 600 }}
        requiredMark={false}
        layout='vertical'
        validateMessages={validateMessages}
      >
        <RowSection>Endereço</RowSection>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.cep}>
              <Input
                maxLength={9}
                min={0}
                placeholder='00000-000'
                onChange={handleCepChange}
                onBlur={(value) => getAddressByCEP(value)}
              />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.state}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                onChange={(value) => onChangeState(value)}
                popupMatchSelectWidth={isUnder}
                placement='bottomLeft'
              >
                {data.states.map((state) => (
                  <Option
                    key={`state-${state.id}`}
                    value={state.id}
                  >
                    {state.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.city}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {data.cities.map((city) => (
                  <Option
                    key={`city-${city.id}`}
                    value={city.id}
                  >
                    {city.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.country}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
                placement='bottomLeft'
              >
                {data.countries.map((country) => (
                  <Option
                    key={`country-${country.id}`}
                    value={country.id}
                  >
                    {country.country}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.neighborhood}>
              <Input placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.streetType}>
              <Select
                placeholder='Selecione uma opção'
                showSearch
                optionFilterProp='children'
                allowClear
                filterOption={(input, option) => (
                  option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                )}
                popupMatchSelectWidth={isUnder}
              >
                {data.typePublicPlace.map((publicPlace) => (
                  <Option
                    key={`type-pubic-place-${publicPlace.id}`}
                    value={publicPlace.id}
                  >
                    {publicPlace.description}
                  </Option>
                ))}
              </Select>
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={24} md={16} lg={16}>
            <AdmissionFormItem fieldRule={formRules.street}>
              <Input placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>

          <Col xs={24} sm={24} md={8} lg={8}>
            <AdmissionFormItem fieldRule={formRules.number}>
              <InputNumberFull type='number' placeholder='Digite aqui'></InputNumberFull>
            </AdmissionFormItem>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <AdmissionFormItem fieldRule={formRules.complement}>
              <Input placeholder='Digite aqui' />
            </AdmissionFormItem>
          </Col>
        </Row>
      </Form>

      <Row>
        <ButtonCol span={24}> <Button type={'primary'}
          onClick={() => handleNext()}>Prosseguir</Button></ButtonCol>
      </Row>
    </>
  );
});

Address.displayName = 'Address';


export default Address;
